'use client';

import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from 'next-intl';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { FormInput } from '@/components/forms/form-input';
import { FormTextarea } from '@/components/forms/form-textarea';
import { Link, useRouter } from '@/i18n/routing';
import { useAccessibleRoute } from '@/hooks/use-route-access';
import { ApiError } from '@/lib/api/client';
import { createStorageLocationSchema } from '../schemas';
import { useCreateStorageLocation } from '../services/storage-locations.mutations';

export function StorageLocationAddForm() {
  const t = useTranslations('storageLocations');
  const tv = useTranslations('storageLocations.validation');
  const router = useRouter();
  const backTo = useAccessibleRoute();

  const schema = useMemo(
    () => createStorageLocationSchema((key) => tv(key)),
    [tv]
  );

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      capacity: '',
      notes: ''
    }
  });

  const createMutation = useCreateStorageLocation();

  const onSubmit = async (data: any) => {
    try {
      await createMutation.mutateAsync({
        name: data.name,
        capacity: Number(data.capacity),
        notes: data.notes || null
      });
      router.push(backTo('/dashboard/storage-locations'));
    } catch (error) {
      if (
        error instanceof ApiError &&
        error.status === 422 &&
        (error.data as any)?.errors?.name
      ) {
        form.setError('name', {
          message: (error.data as any).errors.name[0]
        });
      }
    }
  };

  return (
    <div className='space-y-6'>
      <div className='flex items-start justify-between'>
        <div>
          <p className='text-muted-foreground text-sm'>
            <Link
              href={backTo('/dashboard/storage-locations')}
              className='hover:text-foreground transition-colors'
            >
              {t('breadcrumbs.storageLocations')}
            </Link>
            {' / '}
            {t('addNew')}
          </p>
          <h1 className='mt-1 text-2xl font-bold'>{t('addNew')}</h1>
        </div>
        <div className='flex items-center gap-3'>
          <Button
            type='button'
            variant='link'
            className='text-muted-foreground'
            onClick={() => router.push(backTo('/dashboard/storage-locations'))}
            disabled={createMutation.isPending}
          >
            {t('form.discard')}
          </Button>
          <Button
            type='button'
            disabled={createMutation.isPending}
            onClick={form.handleSubmit(onSubmit)}
          >
            {createMutation.isPending ? t('form.saving') : t('form.save')}
          </Button>
        </div>
      </div>

      <Form
        form={form}
        onSubmit={form.handleSubmit(onSubmit)}
        className='max-w-2xl space-y-4'
      >
        <FormInput
          control={form.control}
          name='name'
          label={t('form.locationName')}
          placeholder={t('form.locationNamePlaceholder')}
          required
        />

        <FormField
          control={form.control}
          name='capacity'
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t('form.capacity')}
                <span className='ms-1 text-red-500'>*</span>
              </FormLabel>
              <FormControl>
                <div className='relative'>
                  <Input
                    type='number'
                    placeholder='0'
                    className='pe-16'
                    min={1}
                    {...field}
                    value={field.value ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      field.onChange(v === '' ? '' : parseFloat(v));
                    }}
                  />
                  <span className='text-muted-foreground absolute end-3 top-1/2 -translate-y-1/2 text-xs font-semibold'>
                    {t('form.capacityUnit')}
                  </span>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormTextarea
          control={form.control}
          name='notes'
          label={t('form.notes')}
          placeholder={t('form.notesPlaceholder')}
        />
      </Form>
    </div>
  );
}
